import { useEffect, useCallback } from "react"

function getThemeCssVariableValue(variableName, fallback) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim()
  return value || fallback
}

function createDragImage(columnName) {
  const dragImageElement = document.createElement("div")
  const dragImageBackground = getThemeCssVariableValue("--opcolor-blue-light", "#00abe8")
  const dragImageShadowColor = getThemeCssVariableValue("--app-shared-shadow-color-alpha", "rgba(0, 0, 0, 0.2)")

  dragImageElement.style.cssText = `
    position: absolute;
    top: -1000px;
    left: -1000px;
    padding: 8px 12px;
    background: ${dragImageBackground};
    color: white;
    border-radius: 4px;
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 600;
    white-space: nowrap;
    box-shadow: 0 4px 8px ${dragImageShadowColor};
    z-index: 9999;
  `
  dragImageElement.textContent = columnName

  return dragImageElement
}

/**
 * Hook for customizing the column header drag image in DataGrid
 *
 * This hook subscribes to the columnHeaderDragStart event and replaces the default drag image (which shows buttons and icons and
 * removes the column label, because we're using custom header component) with a clean text-only badge showing just the column header name.
 *
 * @param {Object} apiRef - DataGrid API reference
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether custom drag image is enabled (default: true)
 * @returns {void}
 */
export function useColumnDragImage(apiRef, { enabled = true } = {}) {
  const handleColumnHeaderDragStart = useCallback((params, event) => {
    if (!enabled) return

    const dragImageElement = createDragImage(params.colDef.headerName)

    document.body.appendChild(dragImageElement)

    // Force a reflow to ensure the element is rendered before setting drag image
    dragImageElement.offsetHeight

    event.dataTransfer.setDragImage(dragImageElement, 0, 0)

    // Clean up the temporary element after the drag starts
    setTimeout(() => {
      if (document.body.contains(dragImageElement)) {
        document.body.removeChild(dragImageElement)
      }
    }, 0)
  }, [enabled])

  useEffect(() => {
    if (!enabled || !apiRef.current) return

    const unsubscribe = apiRef.current.subscribeEvent("columnHeaderDragStart", handleColumnHeaderDragStart)

    return unsubscribe
  }, [enabled, apiRef, handleColumnHeaderDragStart])
}
